import type { LocaleCode } from "@/pages/cogo-marketing/i18n";
import type { RailItemId } from "./types";
import type { ChannelMissReason } from "@/features/_shared/channel-types";

type ComposerCopy = Record<RailItemId, string> & {
  documentPreview: string;
  gamePreview: string;
  askAbout: (task: string) => string;
  askToReview: (task: string) => string;
};

interface PersonaCopy {
  essentials: string;
  allTools: string;
  toolVisibility: string;
  /**
   * Shown where the persona is offered OUTSIDE the IDE (Workspace Settings).
   * The IDE's own switcher is a bare control beside the tools it governs; on
   * a settings page the same control needs to say what it changes.
   */
  settingsDetail: string;
}

interface EmptyStateCopy {
  gettingWorkspaceReady: string;
  couldNotCreate: string;
  stopped: string;
  readyToReview: string;
  describeAppToBuild: string;
  describeAppToDraft: string;
  working: string;
  draft: string;
  heroLineOne: string;
  heroLineTwo: string;
  description: string;
  workingPlaceholder: string;
  inputPlaceholder: string;
  preparing: string;
  send: string;
  keepTalking: string;
  conversationCanChange: string;
  pageDidNotPassChecks: string;
  selectExactPart: string;
}

interface ComposePreviewCopy {
  composing: string;
  summary: (ready: number, total: number, pages: number) => string;
}

interface SettingsSurfaceCopy {
  eyebrow: string
  title: string
  reviewSettings: string
  summary: string
  general: string
  projectName: string
  untitledProject: string
  description: string
  descriptionPlaceholder: string
  saved: string
  saveFailed: string
  openLiveProject: string
  metadataLoading: string
  projectNameRequired: string
  saving: string
  saveChanges: string
  slug: string
  projectId: string
  buildTarget: string
  platform: string
  designSystemPreset: string
  noneBound: string
  dangerZone: string
  dangerLead: string
  dangerTail: string
}

interface GenerationProgressCopy {
  defaultTitle: string
  /** Card title once app generation has failed (F9c). */
  failedTitle: string
  ariaLabel: string
  pageUnit: (count: number) => string
  /**
   * The progress card's accessible name, as ONE authored phrase per language.
   *
   * F9(b): this used to be built as `` `${statusLabel}. ${count} ${unit}` ``
   * inside `CoogoMarkCount`, which announced `Generated app. 10 페이지` — an
   * English producer literal glued to a localized unit. Word order and
   * punctuation differ per language (Japanese/Chinese use `。`, Korean needs a
   * counter), so each locale writes its own template instead of the caller
   * concatenating fragments.
   */
  countAnnouncement: (status: string, count: number) => string
  request: string
  source: string
  build: string
  logic: string
  app: string
  launch: string
  toneLabels: Record<'done' | 'active' | 'review' | 'pending', string>
  backgroundJob: {
    queued: string
    generating: string
    reconnecting: string
    paused: string
    continues: string
    resume: string
    resuming: string
    resumeFailed: string
    finalizationPending: string
    finalizationDetail: string
    finalize: string
    finalizeFailed: string
  }
  evidence: {
    source: string
    logic: string
    speed: string
    launch: string
    primitiveCount: (count: number) => string
    conformance: string
    visible: string
    design: string
    compilerValid: string
    runtimeValid: string
    target: string
  }
  statusLabels: Record<
    | 'applied'
    | 'candidate'
    | 'missing'
    | 'pass'
    | 'blocked'
    | 'warn'
    | 'fail'
    | 'metric-only'
    | 'ready'
    | 'not-required'
    | 'realtime'
    | 'interactive'
    | 'slow'
    | 'unknown',
    string
  >
}

/**
 * The creator's release journey (C2).
 *
 * These three steps lived in a module-level English literal array inside
 * `essentials-release-surface.tsx`, so `ko` and `ja` rendered byte-identical
 * to `en` — the PipelineStepper defect below, two directories away, same
 * shell, same persona. `unavailable` is ONE authored phrase per language, not
 * a label plus an English suffix.
 */
export interface ReleaseStepperCopy {
  journey: string
  steps: Record<'review' | 'prepare' | 'launch', { label: string; description: string }>
  unavailable: (label: string) => string
}

/**
 * The cockpit tab signal's accessible names (I3).
 *
 * The status name was `` `${label}: ${state.kind}` `` — a localized tab label
 * spliced onto a raw engine token, so Korean announced "앱: working". The
 * unread badge was hard-coded English AND wrong at one ("1 unreviewed
 * changes").
 */
export type CockpitTabSignalKind =
  | 'idle'
  | 'working'
  | 'changed'
  | 'attention'
  | 'blocked'
  | 'completed'

export interface CockpitTabSignalCopy {
  states: Record<CockpitTabSignalKind, string>
  /**
   * Takes the KIND, not a rendered word: the copy module does the join, so a
   * call site has no raw engine token to splice onto a localized label.
   */
  status: (label: string, kind: CockpitTabSignalKind) => string
  unreviewed: (count: number) => string
}

/** The five conversation steps the chat dock's PipelineStepper shows (F4). */
export type PipelineStepperCopy = Record<
  'describe' | 'scenarios' | 'pages' | 'generate' | 'complete',
  string
>

/**
 * Worklog cards (F3 / F9a).
 *
 * The producer emits a STATE (`titleCode` / entry `code` + counts); this
 * dictionary owns the language. Before this existed, `chat-messages.tsx`
 * rewrote SOME of the producer's English strings into Korean by regex with no
 * locale argument, so one card carried two languages in every session.
 */
export type WorklogTitleCopy = Record<
  | 'webmcp-capabilities'
  | 'production-evidence'
  | 'generation-provenance'
  // Wave 4 lane 1 — the central bus-execution-seam disclosure card
  // (studio-mcp-bus.ts). Not read via this dictionary today (its entries
  // always lack a `code`, so `worklogCardTitle`'s bilingual guard returns
  // the message's own `title` verbatim, which the bus already bakes from
  // `chat-messages-copy.ts`'s `agentDisclosure.title` at the correct
  // locale) — kept here anyway so `WorklogCardId` accepts the literal and
  // so a future code-driven redesign of this card has a correct home.
  | 'agent-action',
  string
>

/**
 * CLASS GUARD for the bilingual card (I1) — and the ONLY place that decides
 * whose language a worklog card speaks.
 *
 * `card` says WHICH card this is. It does NOT say who owns the words: an
 * entry with no `code` is a raw producer line, rendered verbatim by design,
 * and a localized title over verbatim entries is a bilingual card — a Korean
 * title above an English body, which is what `register-studio-tools.ts`
 * shipped when it named a card's copy regardless of its entries.
 *
 * So the rule is: the copy module owns the title only when it owns EVERY
 * entry. If any entry is verbatim, the whole card keeps the producer's title.
 * Uniformly English beats bilingual.
 *
 * Note what this function deliberately does NOT do: it does not let the
 * language decision remove the card's identity. `card` is still set on a card
 * whose words this function declines to own, so the compact-dock visibility
 * filter can still recognise it. The first version of this fix conflated the
 * two and silently dropped the developer's card from the shipping dock.
 */
export function worklogCardTitle(
  card: {
    title: string
    card?: keyof WorklogTitleCopy
    entries: ReadonlyArray<{ code?: keyof WorklogEntryCopy }>
  },
  copy: CoogoIdeChromeCopy,
): string {
  if (!card.card) return card.title
  if (card.entries.some((entry) => !entry.code)) return card.title
  return copy.worklog.titles[card.card]
}

export interface WorklogEntryCounts {
  count: number
  total?: number
}

/**
 * WHAT `actionflow-flows` COUNTS, and why its words say so.
 *
 * The number comes from `countToolsByTag(tools, 'action-flow')`
 * (`mcp/register-studio-tools.ts`), i.e. the WebMCP tools generated by
 * `generateActionFlowTools` (`@cogo/webmcp-runtime`), which emits one tool per
 * flow and SKIPS every flow whose trigger is `init`. So it is the count of
 * flows the agent can invoke — not the count of flows the app has. Measured
 * 2026-08-18: a generated app with 2 flows and 2 event bindings put `1` here,
 * correctly.
 *
 * Every locale used to render that number as `connected` / `연결됨`, which is a
 * claim about the APP's wiring — the one entry on this card that reported a
 * state of the app rather than a capability of the agent, sitting next to
 * `블록 2개` where the mismatch reads as something being broken. The count was
 * never wrong; the words named the wrong thing. They now name the agent, which
 * is also what the card itself ("Screen controls") is about, and matches the
 * `available` / `can be submitted` shape of every sibling entry.
 *
 * `actionflow-flows-names-the-agent.test.ts` locks both halves: the referent
 * (init-triggered flows are excluded from the count) and the wording.
 */
export type WorklogEntryCopy = Record<
  | 'tools-ready'
  | 'navigation-routes'
  | 'actionflow-flows'
  | 'form-tools'
  | 'commerce-tools'
  | 'evidence-backed-blocks'
  | 'verified-references'
  | 'review-transparent'
  | 'review-needs-review'
  | 'review-fallback-hidden'
  | 'provenance-reused'
  | 'provenance-fresh',
  (counts: WorklogEntryCounts) => { label: string; value: string }
>

/**
 * Accessible names for `ResponsivePanel` (F10).
 *
 * Keyed by the panel's own id so there is no call site left to forget: the
 * component looks its own name up. `a11yTitle` survives only as an override.
 * `pixels` is the resize handle's `aria-valuetext`, which was the hardcoded
 * English `` `${n} pixels` `` in every locale.
 */
export interface ResponsivePanelCopy {
  chat: { title: string; description: string; resize: string }
  inspector: { title: string; description: string; resize: string }
  devtools: { title: string; description: string; resize: string }
  pixels: (size: number) => string
}

/**
 * Creator-facing failure copy shared by every failure surface (F8).
 *
 * I2 — this used to carry its OWN `missReason` and `pageDidNotPassChecks`,
 * duplicating `CoogoIdeChromeCopy.missReason` and
 * `EmptyStateCopy.pageDidNotPassChecks`. Two dictionaries for the same nine
 * codes drifted within a single commit (6/9 apart in `ko`, 8/9 in `ja`), so
 * one failure read as two different sentences depending on whether chat or
 * the canvas reported it. There is now exactly one home per string.
 */
export interface FailureCopy {
  selectExactPartDetail: string
}

export interface CoogoIdeChromeCopy {
  surfaceLabels: Record<RailItemId, string>
  persona: PersonaCopy
  emptyState: EmptyStateCopy
  composePreview: ComposePreviewCopy
  settingsSurface: SettingsSurfaceCopy
  generationProgress: GenerationProgressCopy
  pipelineStepper: PipelineStepperCopy
  releaseStepper: ReleaseStepperCopy
  cockpitTabSignal: CockpitTabSignalCopy
  worklog: { titles: WorklogTitleCopy; entries: WorklogEntryCopy }
  panels: ResponsivePanelCopy
  failure: FailureCopy
  /** One authored phrase, never `${close} ${context}` — see F11. */
  closeContext: string
  selectInApp: string
  backToProjects: string
  chat: string
  close: string
  context: string
  details: string
  content: string
  authoring: string
  showChat: string
  conversation: (surface: string) => string
  generating: (name: string) => string
  updating: (name: string, mode: string, status: string) => string
  real: string
  mock: string
  cancel: string
  cancelGeneration: string
  draftingStartingPoint: string
  applyingRequest: string
  navigate: string
  workspace: string
  selectedTarget: string
  changeTarget: string
  currentWorkspace: string
  currentPage: string
  currentItem: string
  workspacePane: string
  workspaceNavigation: string
  workspaceDetails: string
  couldNotLoadProject: string
  /**
   * Chat-dock body for a project that did not open (F13, 2026-08-13). It says
   * the fact, then the consequence for this surface — there is nothing here to
   * change or publish — then the one thing that works.
   */
  projectNotOpenNothingToChange: string
  /** Publish's `title` while a project load has failed. */
  publishNeedsLoadedProject: string
  /**
   * Appended to the blocked Publish control's accessible description to say
   * where the resolution lives.
   *
   * The readiness gate has always authored a `detail` and a resolution
   * `action` per blocked reason (publish-gate-copy.ts), but both render only
   * inside PublishGateRows on the Publish surface — the chrome control could
   * carry the title and nothing else. No string existed for "and here is
   * where to go" because the tooltip was treated as terminal: the design
   * never had the blocked control act as an entry point, so nobody needed to
   * name the destination.
   */
  publishBlockedGuidance: string
  readinessGateFailed: string
  connected: string
  ready: string
  saving: string
  unsaved: string
  saveFailed: string
  conflict: string
  statusReady: string
  statusSaving: string
  statusUnsaved: string
  statusSaveFailed: string
  statusConflict: string
  toggleStatusDetails: string
  currentWorkspaceMode: string
  messageCoogo: string
  askCoogo: string
  /** Trigger label for the Source toolbar's consolidated review-actions menu. */
  sourceReviewMenu: string
  selectSomething: string
  pickTargetHint: string
  openThisWork: string
  noWorkToPick: string
  askAboutThis: string
  sendCurrentItem: string
  viewDetails: string
  openDeeperModel: string
  contextHint: string
  screen: string
  screenCount: (current: number, total: number) => string
  editScreen: string
  searchScreens: string
  noScreensFound: string
  generatedApp: string
  generatingApp: string
  previewNotReadyTitle: string
  previewNotReadyDescription: string
  previewPreparingTitle: string
  previewPreparingDescription: string
  previewUnavailableTitle: string
  previewUnavailableDescription: string
  selectedForChat: string
  noPartialResult: string
  checksFailed: (pageId?: string) => string
  tryAgain: string
  startOver: string
  missReason: Record<ChannelMissReason, string>
  composer: ComposerCopy
}

const EN_COCKPIT_STATES: CockpitTabSignalCopy["states"] = {
  idle: "idle",
  working: "working",
  changed: "changed",
  attention: "needs attention",
  blocked: "blocked",
  completed: "completed",
}

const KO_COCKPIT_STATES: CockpitTabSignalCopy["states"] = {
  idle: "대기 중",
  working: "작업 중",
  changed: "변경됨",
  attention: "확인 필요",
  blocked: "막힘",
  completed: "완료됨",
}

const JA_COCKPIT_STATES: CockpitTabSignalCopy["states"] = {
  idle: "待機中",
  working: "作業中",
  changed: "変更あり",
  attention: "要確認",
  blocked: "停止中",
  completed: "完了",
}

const ZH_COCKPIT_STATES: CockpitTabSignalCopy["states"] = {
  idle: "空闲",
  working: "进行中",
  changed: "有变更",
  attention: "需要确认",
  blocked: "已阻塞",
  completed: "已完成",
}

const ZH_TW_COCKPIT_STATES: CockpitTabSignalCopy["states"] = {
  idle: "閒置",
  working: "進行中",
  changed: "有變更",
  attention: "需要確認",
  blocked: "已阻擋",
  completed: "已完成",
}

const enComposer: ComposerCopy = {
  preview: "Describe the next app change",
  source: "Review a source or frame",
  editor: "Change the selected page or element",
  behavior: "Add or change an event or flow",
  data: "Change a data source or binding",
  evidence: "Name a claim to verify",
  report: "Change a status report or export",
  document: "Change the selected document page",
  publish: "Review release readiness",
  deploy: "Check a deployment target",
  library: "Inspect a reusable item",
  map: "Change a page relationship",
  code: "Inspect or change Coogo JSON",
  settings: "Change a project setting",
  documentPreview: "Describe the next document change",
  gamePreview: "Describe the next game change",
  askAbout: (task) => `Describe what you need in ${task.toLocaleLowerCase("en")}`,
  askToReview: (task) => `Describe what you want reviewed in ${task.toLocaleLowerCase("en")}`,
};

const en: CoogoIdeChromeCopy = {
  surfaceLabels: {
    preview: "App",
    source: "Design",
    editor: "Pages",
    behavior: "Action",
    data: "Data",
    evidence: "Checks",
    report: "Status",
    document: "Guide",
    publish: "Publish",
    deploy: "Launch",
    library: "Details",
    map: "Map",
    code: "Code",
    settings: "Settings",
  },
  settingsSurface: {
    eyebrow: 'Settings · Project',
    title: 'Project settings',
    reviewSettings: 'Review settings',
    summary: "Edit the project's canonical name and description before publishing.",
    general: 'General',
    projectName: 'Project name',
    untitledProject: 'Untitled project',
    description: 'Description',
    descriptionPlaceholder: 'What should collaborators know about this app?',
    saved: 'Saved',
    saveFailed: 'Could not save project settings',
    openLiveProject: 'Open a live project to edit settings',
    metadataLoading: 'Project metadata is still loading',
    projectNameRequired: 'Project name is required',
    saving: 'Saving',
    saveChanges: 'Save changes',
    slug: 'Slug',
    projectId: 'Project ID',
    buildTarget: 'Build target',
    platform: 'Platform',
    designSystemPreset: 'Design system preset',
    noneBound: '(none bound)',
    dangerZone: 'Danger zone',
    dangerLead: 'Project deletion and archive actions are intentionally not exposed in this workspace. Use',
    dangerTail: ' in the left rail to archive or remove a project.',
  },
  generationProgress: {
    defaultTitle: 'Generating...',
    failedTitle: 'App generation failed',
    ariaLabel: 'Generation progress stages',
    pageUnit: (count) => count === 1 ? 'page' : 'pages',
    countAnnouncement: (status, count) =>
      `${status}. ${count} ${count === 1 ? 'page' : 'pages'}`,
    request: 'Request',
    source: 'Source',
    build: 'Build',
    logic: 'Logic',
    app: 'App',
    launch: 'Launch',
    toneLabels: { done: 'Done', active: 'Active', review: 'Review', pending: 'Pending' },
    backgroundJob: {
      queued: 'Queued',
      generating: 'Generating',
      reconnecting: 'Reconnecting',
      paused: 'Ready to reconnect',
      continues: 'You can leave Studio; generation will continue.',
      resume: 'Resume updates',
      resuming: 'Resuming...',
      resumeFailed: 'Could not resume generation updates.',
      finalizationPending: 'App saved · verification pending',
      finalizationDetail: 'Finish the durable evidence transition. The app will not be generated again.',
      finalize: 'Finish verification',
      finalizeFailed: 'Could not finish verification. Your saved app was not regenerated.',
    },
    evidence: {
      source: 'Source',
      logic: 'Logic',
      speed: 'Speed',
      launch: 'Launch',
      primitiveCount: (count) => `${count} primitives`,
      conformance: 'conformance',
      visible: 'visible',
      design: 'design',
      compilerValid: 'compiler valid',
      runtimeValid: 'runtime valid',
      target: 'target',
    },
    statusLabels: {
      applied: 'applied',
      candidate: 'candidate',
      missing: 'missing',
      pass: 'pass',
      blocked: 'blocked',
      warn: 'warn',
      fail: 'fail',
      'metric-only': 'metric only',
      ready: 'ready',
      'not-required': 'not required',
      realtime: 'realtime',
      interactive: 'interactive',
      slow: 'slow',
      unknown: 'unknown',
    },
  },
  persona: {
    essentials: "Essentials",
    allTools: "All tools",
    toolVisibility: "Tool visibility",
    settingsDetail:
      "Choose how much of the workspace the tools show. This is a preference of yours, not of one project, and you can change it at any time.",
  },
  emptyState: {
    gettingWorkspaceReady: "Getting the workspace ready.",
    couldNotCreate: "Could not create it.",
    stopped: "Stopped.",
    readyToReview: "Ready to review.",
    describeAppToBuild: "Describe the app you want to build.",
    describeAppToDraft: "Describe the app you want to draft.",
    working: "Working",
    draft: "Draft",
    heroLineOne: "Your one line.",
    heroLineTwo: "A useful starting point.",
    description:
      "Describe the application you want. Coogo will turn it into a working starting point.",
    workingPlaceholder: "Coogo is working...",
    inputPlaceholder: "Describe the outcome, audience, and constraints.",
    preparing: "Preparing...",
    send: "Send",
    keepTalking: "Keep talking.",
    conversationCanChange: "The app can change with the conversation.",
    pageDidNotPassChecks: "That page didn't pass our checks",
    selectExactPart: "Select the exact part to change",
  },
  composePreview: {
    composing: "Composing from retrieved blocks…",
    summary: (ready, total, pages) =>
      `${ready}/${total} candidates ready · ${pages} ${pages === 1 ? "page" : "pages"} planned`,
  },
  backToProjects: "Back to projects",
  chat: "Chat",
  close: "Close",
  context: "Context",
  closeContext: "Close context",
  selectInApp: "Select in app",
  pipelineStepper: {
    describe: "Describe",
    scenarios: "Scenarios",
    pages: "Pages",
    generate: "Generate",
    complete: "Complete",
  },
  releaseStepper: {
    journey: "Release journey",
    steps: {
      review: {
        label: "Review",
        description: "Check what is ready and what still needs attention.",
      },
      prepare: {
        label: "Prepare",
        description: "Create the exact candidate that can be approved.",
      },
      launch: {
        label: "Launch",
        description: "Authorize, deploy, and verify the released result.",
      },
    },
    unavailable: (label) => `${label} — unavailable until review passes`,
  },
  cockpitTabSignal: {
    states: EN_COCKPIT_STATES,
    status: (label, kind) => `${label}: ${EN_COCKPIT_STATES[kind]}`,
    unreviewed: (count) => `${count} unreviewed ${count === 1 ? "change" : "changes"}`,
  },
  worklog: {
    titles: {
      "webmcp-capabilities": "Screen controls",
      "production-evidence": "Launch check",
      "generation-provenance": "How this was made",
      "agent-action": "Agent action completed",
    },
    entries: {
      "tools-ready": ({ count }) => ({
        label: "Controls",
        value: `${count} ready`,
      }),
      "navigation-routes": ({ count }) => ({
        label: "Navigation",
        value: `${count} ${count === 1 ? "route" : "routes"} available`,
      }),
      "actionflow-flows": ({ count }) => ({
        label: "Behaviour",
        value: `${count} ${count === 1 ? "behaviour" : "behaviours"} the agent can run`,
      }),
      "form-tools": ({ count }) => ({
        label: "Forms",
        value: `${count} ${count === 1 ? "form" : "forms"} can be submitted`,
      }),
      "commerce-tools": ({ count }) => ({
        label: "Cart",
        value: `${count} cart ${count === 1 ? "action" : "actions"} available`,
      }),
      "evidence-backed-blocks": ({ count, total }) => ({
        label: "Checked",
        value: `${count} of ${total ?? count} ${
          (total ?? count) === 1 ? "part has" : "parts have"
        } checked evidence`,
      }),
      "verified-references": ({ count }) => ({
        label: "Sources",
        value: `${count} verified ${count === 1 ? "reference" : "references"} used`,
      }),
      "review-transparent": () => ({
        label: "Review",
        value: "How this was made is visible",
      }),
      "review-needs-review": () => ({
        label: "Review",
        value: "Review this app before you publish it",
      }),
      "review-fallback-hidden": () => ({
        label: "Review",
        value: "Some of how this was made is not visible yet",
      }),
      "provenance-reused": ({ count }) => ({
        label: "Reused",
        value: `${count} ${count === 1 ? "part reuses" : "parts reuse"} a verified design`,
      }),
      "provenance-fresh": ({ count }) => ({
        label: "New",
        value: `${count} ${count === 1 ? "part is a newly created design" : "parts are newly created designs"}`,
      }),
    },
  },
  panels: {
    chat: {
      title: "Chat",
      description: "Chat with Coogo about this workspace",
      resize: "Resize the chat panel",
    },
    inspector: {
      title: "Details",
      description: "Details of the current selection",
      resize: "Resize the details panel",
    },
    devtools: {
      title: "Developer tools",
      description: "Developer tools for this project",
      resize: "Resize the developer tools panel",
    },
    pixels: (size) => `${size} pixels`,
  },
  failure: {
    selectExactPartDetail:
      "Open the app, select the text or control, then try again.",
  },
  details: "Details",
  content: "Content",
  authoring: "Authoring",
  showChat: "Show chat",
  conversation: (surface) => `${surface} conversation`,
  generating: (name) => `Generating: ${name}`,
  updating: (name, mode, status) => `Updating ${name} · ${mode} · ${status}`,
  real: "Real",
  mock: "Mock",
  cancel: "Cancel",
  cancelGeneration: "Cancel generation",
  draftingStartingPoint: "Drafting a starting point.",
  applyingRequest: "Applying your request.",
  navigate: "Navigate",
  workspace: "Workspace",
  selectedTarget: 'Selected target',
  changeTarget: 'Change',
  currentWorkspace: 'Current workspace',
  currentPage: 'Current page',
  currentItem: 'Current item',
  workspacePane: "Workspace pane",
  workspaceNavigation: "Workspace navigation",
  workspaceDetails: "Workspace details",
  couldNotLoadProject: "Couldn’t load project",
  projectNotOpenNothingToChange:
    "This project did not open, so there is nothing here to change or publish. Go back to your projects and open one from there.",
  publishNeedsLoadedProject:
    "This project did not open, so there is nothing to publish",
  publishBlockedGuidance:
    "Open Publish to see what to fix and how to resolve it.",
  readinessGateFailed: "Readiness gate failed",
  connected: "Connected",
  ready: "Ready",
  saving: "Saving",
  unsaved: "Unsaved",
  saveFailed: "Save failed",
  conflict: "Conflict",
  statusReady: "Status: ready",
  statusSaving: "Status: saving",
  statusUnsaved: "Status: unsaved changes",
  statusSaveFailed: "Status: save failed",
  statusConflict: "Status: save conflict",
  toggleStatusDetails: "Toggle status details",
  currentWorkspaceMode: "Current workspace mode",
  messageCoogo: "Message Coogo",
  askCoogo: "Ask Coogo",
  sourceReviewMenu: "Review",
  selectSomething: "Select something in the app.",
  pickTargetHint:
    "Pick the work you want to act on. Its pages, behavior, and details open once it is open.",
  openThisWork: "Open this work",
  noWorkToPick:
    "No saved work yet. Start one from Home and it will appear here.",
  askAboutThis: "Ask about this",
  sendCurrentItem: "Send the current item to chat.",
  viewDetails: "View details",
  openDeeperModel: "Open the deeper project model.",
  contextHint:
    "Ask in chat keeps the conversation open. Other actions move to a focused work panel only when needed.",
  screen: "Screen",
  screenCount: (current, total) => `${current} of ${total}`,
  editScreen: "Edit screen",
  searchScreens: "Search screens",
  noScreensFound: "No screens found",
  generatedApp: "Generated app",
  generatingApp: "Generating app",
  previewNotReadyTitle: "Preview is not ready yet",
  previewNotReadyDescription:
    "This project has no page and block pair attached. Open Design to attach a page, or use Chat to create the missing app surface.",
  previewPreparingTitle: "Preparing preview",
  previewPreparingDescription:
    "Coogo is attaching this app to its isolated preview surface. The preview stays here until it is ready to render.",
  previewUnavailableTitle: "Preview renderer is unavailable",
  previewUnavailableDescription:
    "Coogo could not start the preview renderer. Open Design to check the page and block connection, then use Chat to repair the preview.",
  selectedForChat: "Selected for chat",
  noPartialResult: "No partial result",
  checksFailed: (pageId) =>
    pageId ? `Checks failed on page “${pageId}”` : "Checks failed",
  tryAgain: "Try again",
  startOver: "Start over",
  missReason: {
    "no-candidates": "Couldn't find a matching pattern",
    "no-template": "That type isn’t ready yet",
    "no-binding-schema": "Can’t recognize the data shape",
    "kg-init-failed": "Please try again in a moment",
    "bind-throw": "Something went wrong processing it",
    "wasm-unavailable": "Your browser doesn’t support this",
    aborted: "Cancelled",
    "unsupported-mode": "Unsupported mode",
    "backend-failed": "Server connection failed",
  },
  composer: enComposer,
};

const ko: CoogoIdeChromeCopy = {
  ...en,
  surfaceLabels: {
    preview: "앱",
    source: "디자인",
    editor: "페이지",
    behavior: "동작",
    data: "데이터",
    evidence: "검사",
    report: "현황",
    document: "가이드",
    publish: "발행",
    deploy: "출시",
    library: "세부 정보",
    map: "맵",
    code: "코드",
    settings: "설정",
  },
  settingsSurface: {
    eyebrow: '설정 · 프로젝트',
    title: '프로젝트 설정',
    reviewSettings: '설정 검토',
    summary: '게시하기 전에 프로젝트의 기준 이름과 설명을 편집합니다.',
    general: '일반',
    projectName: '프로젝트 이름',
    untitledProject: '제목 없는 프로젝트',
    description: '설명',
    descriptionPlaceholder: '협업자가 이 앱에 대해 알아야 할 내용을 입력하세요.',
    saved: '저장됨',
    saveFailed: '프로젝트 설정을 저장하지 못했습니다',
    openLiveProject: '설정을 편집하려면 실제 프로젝트를 여세요',
    metadataLoading: '프로젝트 정보를 불러오는 중입니다',
    projectNameRequired: '프로젝트 이름을 입력해야 합니다',
    saving: '저장 중',
    saveChanges: '변경 사항 저장',
    slug: '슬러그',
    projectId: '프로젝트 ID',
    buildTarget: '빌드 대상',
    platform: '플랫폼',
    designSystemPreset: '디자인 시스템 프리셋',
    noneBound: '(연결 없음)',
    dangerZone: '주의 영역',
    dangerLead: '이 작업 공간에서는 프로젝트 삭제 및 보관 기능을 제공하지 않습니다. 왼쪽 레일의',
    dangerTail: '를 사용해 프로젝트를 보관하거나 삭제하세요.',
  },
  generationProgress: {
    defaultTitle: '생성 중...',
    failedTitle: '앱 생성에 실패했습니다',
    ariaLabel: '생성 진행 단계',
    pageUnit: () => '페이지',
    countAnnouncement: (status, count) => `${status}. 페이지 ${count}개`,
    request: '요청',
    source: '자료',
    build: '구성',
    logic: '동작',
    app: '앱',
    launch: '출시',
    toneLabels: { done: '완료', active: '진행 중', review: '검토 필요', pending: '대기 중' },
    backgroundJob: {
      queued: '대기 중',
      generating: '생성 중',
      reconnecting: '다시 연결 중',
      paused: '다시 연결할 수 있음',
      continues: 'Studio를 나가도 생성은 계속됩니다.',
      resume: '진행 상황 다시 보기',
      resuming: '다시 연결 중...',
      resumeFailed: '생성 진행 상황을 다시 연결하지 못했습니다.',
      finalizationPending: '앱 저장 완료 · 검증 대기 중',
      finalizationDetail: '불변 증거 전환만 마무리합니다. 앱을 다시 생성하지 않습니다.',
      finalize: '검증 마무리',
      finalizeFailed: '검증을 마무리하지 못했습니다. 저장된 앱은 다시 생성하지 않았습니다.',
    },
    evidence: {
      source: '자료',
      logic: '동작',
      speed: '속도',
      launch: '출시',
      primitiveCount: (count) => `기본 요소 ${count}개`,
      conformance: '적합성',
      visible: '표시',
      design: '디자인',
      compilerValid: '컴파일러 유효',
      runtimeValid: '런타임 유효',
      target: '목표',
    },
    statusLabels: {
      applied: '적용됨',
      candidate: '후보',
      missing: '누락',
      pass: '통과',
      blocked: '차단됨',
      warn: '주의',
      fail: '실패',
      'metric-only': '지표만',
      ready: '준비됨',
      'not-required': '불필요',
      realtime: '실시간',
      interactive: '대화형',
      slow: '느림',
      unknown: '알 수 없음',
    },
  },
  persona: {
    essentials: "필수 도구",
    allTools: "모든 도구",
    toolVisibility: "도구 표시 범위",
    settingsDetail:
      "작업 공간의 도구를 얼마나 보여줄지 고릅니다. 프로젝트가 아니라 사용자 본인의 설정이며, 언제든 바꿀 수 있습니다.",
  },
  emptyState: {
    gettingWorkspaceReady: "작업 공간을 준비하고 있습니다.",
    couldNotCreate: "결과를 만들지 못했습니다.",
    stopped: "중단되었습니다.",
    readyToReview: "검토할 준비가 되었습니다.",
    describeAppToBuild: "만들고 싶은 앱을 설명하세요.",
    describeAppToDraft: "초안을 만들 앱을 설명하세요.",
    working: "작업 중",
    draft: "초안",
    heroLineOne: "한 줄로 시작하세요.",
    heroLineTwo: "쓸 수 있는 시작점이 됩니다.",
    description:
      "원하는 앱을 설명하면 Coogo가 실제로 다듬어 갈 수 있는 시작점으로 만듭니다.",
    workingPlaceholder: "Coogo가 작업하고 있습니다...",
    inputPlaceholder: "결과, 대상 사용자, 제약 조건을 설명하세요.",
    preparing: "준비 중...",
    send: "보내기",
    keepTalking: "계속 대화하세요.",
    conversationCanChange: "대화로 앱을 계속 바꿀 수 있습니다.",
    pageDidNotPassChecks: "페이지가 검사를 통과하지 못했습니다",
    selectExactPart: "변경할 부분을 정확히 선택하세요",
  },
  composePreview: {
    composing: "가져온 블록을 조합하고 있습니다…",
    summary: (ready, total, pages) =>
      `${ready}/${total}개 후보 준비됨 · ${pages}개 페이지 계획됨`,
  },
  backToProjects: "프로젝트로 돌아가기",
  chat: "채팅",
  close: "닫기",
  context: "컨텍스트",
  closeContext: "컨텍스트 닫기",
  selectInApp: "앱에서 선택",
  pipelineStepper: {
    describe: "설명",
    scenarios: "시나리오",
    pages: "화면",
    generate: "생성",
    complete: "완료",
  },
  releaseStepper: {
    journey: "출시 절차",
    steps: {
      review: {
        label: "확인",
        description: "준비된 것과 아직 확인이 필요한 것을 봅니다.",
      },
      prepare: {
        label: "준비",
        description: "승인받을 후보를 그대로 만듭니다.",
      },
      launch: {
        label: "출시",
        description: "승인하고 배포한 뒤 결과를 확인합니다.",
      },
    },
    unavailable: (label) => `${label}(확인을 통과해야 열립니다)`,
  },
  cockpitTabSignal: {
    states: KO_COCKPIT_STATES,
    status: (label, kind) => `${label}: ${KO_COCKPIT_STATES[kind]}`,
    unreviewed: (count) => `확인하지 않은 변경 ${count}건`,
  },
  worklog: {
    titles: {
      "webmcp-capabilities": "화면 제어",
      "production-evidence": "출시 확인",
      "generation-provenance": "만들어진 방식",
      "agent-action": "에이전트 작업 완료",
    },
    entries: {
      "tools-ready": ({ count }) => ({
        label: "기능",
        value: `${count}개 준비됨`,
      }),
      "navigation-routes": ({ count }) => ({
        label: "화면 이동",
        value: `경로 ${count}개 사용 가능`,
      }),
      "actionflow-flows": ({ count }) => ({
        label: "동작",
        value: `에이전트가 실행할 수 있는 동작 ${count}개`,
      }),
      "form-tools": ({ count }) => ({
        label: "입력 양식",
        value: `${count}개 제출 가능`,
      }),
      "commerce-tools": ({ count }) => ({
        label: "장바구니",
        value: `장바구니 동작 ${count}개 사용 가능`,
      }),
      "evidence-backed-blocks": ({ count, total }) => ({
        label: "확인됨",
        value: `${total ?? count}개 중 ${count}개에 확인된 근거가 있습니다`,
      }),
      "verified-references": ({ count }) => ({
        label: "자료",
        value: `검증된 참고 자료 ${count}개 사용`,
      }),
      "review-transparent": () => ({
        label: "검토",
        value: "만들어진 과정을 확인할 수 있습니다",
      }),
      "review-needs-review": () => ({
        label: "검토",
        value: "게시하기 전에 이 앱을 검토해 주세요",
      }),
      "review-fallback-hidden": () => ({
        label: "검토",
        value: "만들어진 과정 일부를 아직 확인할 수 없습니다",
      }),
      "provenance-reused": ({ count }) => ({
        label: "재사용",
        value: `${count}개 부분이 검증된 디자인을 재사용합니다`,
      }),
      "provenance-fresh": ({ count }) => ({
        label: "새로 만듦",
        value: `${count}개 부분은 새로 만든 디자인입니다`,
      }),
    },
  },
  panels: {
    chat: {
      title: "채팅",
      description: "이 작업 공간에 대해 Coogo와 대화합니다",
      resize: "채팅 패널 크기 조절",
    },
    inspector: {
      title: "세부 정보",
      description: "현재 선택한 대상의 세부 정보",
      resize: "세부 정보 패널 크기 조절",
    },
    devtools: {
      title: "개발자 도구",
      description: "이 프로젝트의 개발자 도구",
      resize: "개발자 도구 패널 크기 조절",
    },
    pixels: (size) => `${size}픽셀`,
  },
  failure: {
    selectExactPartDetail:
      "앱을 열고 바꿀 문구나 컨트롤을 선택한 뒤 다시 시도해 주세요.",
  },
  details: "세부 정보",
  content: "콘텐츠",
  authoring: "템플릿 저작",
  showChat: "채팅 보기",
  conversation: (surface) => `${surface} 대화`,
  generating: (name) => `생성 중: ${name}`,
  updating: (name, mode, status) => `${name} 업데이트 중 · ${mode} · ${status}`,
  real: "실제",
  mock: "모의",
  cancel: "취소",
  cancelGeneration: "생성 취소",
  draftingStartingPoint: "시작안을 준비하고 있습니다.",
  applyingRequest: "요청을 반영하고 있습니다.",
  navigate: "탐색",
  workspace: "작업 공간",
  selectedTarget: '선택한 대상',
  changeTarget: '변경',
  currentWorkspace: '현재 작업 공간',
  currentPage: '현재 페이지',
  currentItem: '현재 항목',
  workspacePane: "작업 공간 패널",
  workspaceNavigation: "작업 공간 탐색",
  workspaceDetails: "작업 공간 세부 정보",
  couldNotLoadProject: "프로젝트를 불러오지 못했습니다",
  projectNotOpenNothingToChange:
    "이 프로젝트가 열리지 않아서 여기에는 바꾸거나 게시할 대상이 없습니다. 프로젝트 목록으로 돌아가 거기에서 열어 주세요.",
  publishNeedsLoadedProject:
    "이 프로젝트가 열리지 않아서 게시할 대상이 없습니다",
  publishBlockedGuidance: "게시 화면에서 해결 방법을 확인하세요.",
  readinessGateFailed: "준비 상태 검사를 통과하지 못했습니다",
  connected: "연결됨",
  ready: "준비됨",
  saving: "저장 중",
  unsaved: "저장 안 됨",
  saveFailed: "저장 실패",
  conflict: "충돌",
  statusReady: "상태: 준비됨",
  statusSaving: "상태: 저장 중",
  statusUnsaved: "상태: 저장하지 않은 변경",
  statusSaveFailed: "상태: 저장 실패",
  statusConflict: "상태: 저장 충돌",
  toggleStatusDetails: "상태 세부 정보 열기/닫기",
  currentWorkspaceMode: "현재 작업 공간 모드",
  messageCoogo: "Coogo에게 메시지 보내기",
  askCoogo: "Coogo에게 질문",
  sourceReviewMenu: "리뷰",
  selectSomething: "앱에서 항목을 선택하세요.",
  pickTargetHint:
    "작업할 대상을 고르세요. 열고 나면 화면·동작·세부 정보를 다룰 수 있습니다.",
  openThisWork: "이 작업 열기",
  noWorkToPick:
    "저장된 작업이 없습니다. 홈에서 시작하면 여기에 나타납니다.",
  askAboutThis: "이 항목 질문하기",
  sendCurrentItem: "현재 항목을 채팅으로 보냅니다.",
  viewDetails: "세부 정보 보기",
  openDeeperModel: "더 깊은 프로젝트 모델을 엽니다.",
  contextHint:
    "채팅으로 질문하면 대화를 유지합니다. 다른 동작은 필요할 때만 집중 작업 패널로 이동합니다.",
  screen: "화면",
  screenCount: (current, total) => `${total}개 중 ${current}`,
  editScreen: "화면 편집",
  searchScreens: "화면 검색",
  noScreensFound: "일치하는 화면이 없습니다",
  generatedApp: "생성된 앱",
  generatingApp: "앱 생성 중",
  previewNotReadyTitle: "미리보기를 아직 준비할 수 없습니다",
  previewNotReadyDescription:
    "이 프로젝트에는 페이지와 블록 연결이 없습니다. Design에서 페이지를 연결하거나 Coogo에게 누락된 앱 화면 생성을 요청하세요.",
  previewPreparingTitle: "미리보기를 준비하고 있습니다",
  previewPreparingDescription:
    "Coogo가 이 앱을 격리된 미리보기 화면에 연결하고 있습니다. 렌더링 준비가 끝날 때까지 이 상태를 표시합니다.",
  previewUnavailableTitle: "미리보기 렌더러를 사용할 수 없습니다",
  previewUnavailableDescription:
    "Coogo가 미리보기 렌더러를 시작하지 못했습니다. Design에서 페이지와 블록 연결을 확인한 뒤 Coogo에게 미리보기 복구를 요청하세요.",
  selectedForChat: "채팅 대상으로 선택됨",
  noPartialResult: "부분 결과가 없습니다",
  checksFailed: (pageId) =>
    pageId ? `“${pageId}” 페이지 검사 실패` : "검사 실패",
  tryAgain: "다시 시도",
  startOver: "처음부터 다시",
  missReason: {
    "no-candidates": "일치하는 패턴을 찾지 못했습니다",
    "no-template": "아직 준비되지 않은 유형입니다",
    "no-binding-schema": "데이터 구조를 인식하지 못했습니다",
    "kg-init-failed": "잠시 후 다시 시도해 주세요",
    "bind-throw": "처리 중 문제가 발생했습니다",
    "wasm-unavailable": "이 브라우저에서는 지원되지 않습니다",
    aborted: "취소됨",
    "unsupported-mode": "지원하지 않는 모드입니다",
    "backend-failed": "서버 연결에 실패했습니다",
  },
  composer: {
    preview: "다음 앱 변경을 설명하세요",
    source: "소스 또는 프레임 검토",
    editor: "선택한 페이지 또는 요소 변경",
    behavior: "이벤트 또는 흐름 추가·변경",
    data: "데이터 소스 또는 바인딩 변경",
    evidence: "검증할 주장을 입력하세요",
    report: "현황 보고서 또는 내보내기 변경",
    document: "선택한 문서 페이지 변경",
    publish: "릴리스 준비 상태 검토",
    deploy: "배포 대상 확인",
    library: "재사용 항목 검토",
    map: "페이지 관계 변경",
    code: "Coogo JSON 검토·변경",
    settings: "프로젝트 설정 변경",
    documentPreview: "다음 문서 변경을 설명하세요",
    gamePreview: "다음 게임 변경을 설명하세요",
    askAbout: (task) => `${task}에서 필요한 내용을 입력하세요`,
    askToReview: (task) => `${task}에서 검토할 내용을 입력하세요`,
  },
};

const ja: CoogoIdeChromeCopy = {
  ...en,
  surfaceLabels: {
    preview: "アプリ",
    source: "デザイン",
    editor: "ページ",
    behavior: "アクション",
    data: "データ",
    evidence: "確認",
    report: "状況",
    document: "ガイド",
    publish: "公開",
    deploy: "リリース",
    library: "詳細",
    map: "マップ",
    code: "コード",
    settings: "設定",
  },
  settingsSurface: {
    eyebrow: '設定 · プロジェクト',
    title: 'プロジェクト設定',
    reviewSettings: '設定を確認',
    summary: '公開前に、プロジェクトの基準となる名前と説明を編集します。',
    general: '一般',
    projectName: 'プロジェクト名',
    untitledProject: '無題のプロジェクト',
    description: '説明',
    descriptionPlaceholder: '共同作業者に伝えたい内容を入力してください。',
    saved: '保存済み',
    saveFailed: 'プロジェクト設定を保存できませんでした',
    openLiveProject: '設定を編集するには実際のプロジェクトを開いてください',
    metadataLoading: 'プロジェクト情報を読み込んでいます',
    projectNameRequired: 'プロジェクト名は必須です',
    saving: '保存中',
    saveChanges: '変更を保存',
    slug: 'スラッグ',
    projectId: 'プロジェクト ID',
    buildTarget: 'ビルド先',
    platform: 'プラットフォーム',
    designSystemPreset: 'デザインシステムプリセット',
    noneBound: '(未設定)',
    dangerZone: '注意が必要な操作',
    dangerLead: 'このワークスペースでは、プロジェクトの削除とアーカイブ操作を提供していません。左レールの',
    dangerTail: 'からプロジェクトをアーカイブまたは削除してください。',
  },
  generationProgress: {
    defaultTitle: '生成中...',
    failedTitle: 'アプリの生成に失敗しました',
    ariaLabel: '生成の進行段階',
    pageUnit: () => 'ページ',
    countAnnouncement: (status, count) => `${status}。${count}ページ`,
    request: 'リクエスト',
    source: 'ソース',
    build: '構築',
    logic: 'ロジック',
    app: 'アプリ',
    launch: 'リリース',
    toneLabels: { done: '完了', active: '進行中', review: '要確認', pending: '保留' },
    backgroundJob: {
      queued: '待機中',
      generating: '生成中',
      reconnecting: '再接続中',
      paused: '再接続できます',
      continues: 'Studio を離れても生成は続きます。',
      resume: '進行状況を再開',
      resuming: '再接続中...',
      resumeFailed: '生成の進行状況に再接続できませんでした。',
      finalizationPending: 'アプリ保存済み・検証待ち',
      finalizationDetail: '永続的な証拠遷移だけを完了します。アプリは再生成しません。',
      finalize: '検証を完了',
      finalizeFailed: '検証を完了できませんでした。保存済みアプリは再生成していません。',
    },
    evidence: {
      source: 'ソース',
      logic: 'ロジック',
      speed: '速度',
      launch: 'リリース',
      primitiveCount: (count) => `基本要素 ${count}件`,
      conformance: '適合性',
      visible: '表示',
      design: 'デザイン',
      compilerValid: 'コンパイラ有効',
      runtimeValid: 'ランタイム有効',
      target: '目標',
    },
    statusLabels: {
      applied: '適用済み',
      candidate: '候補',
      missing: '不足',
      pass: '合格',
      blocked: 'ブロック',
      warn: '注意',
      fail: '不合格',
      'metric-only': '指標のみ',
      ready: '準備完了',
      'not-required': '不要',
      realtime: 'リアルタイム',
      interactive: 'インタラクティブ',
      slow: '低速',
      unknown: '不明',
    },
  },
  persona: {
    essentials: "基本ツール",
    allTools: "すべてのツール",
    toolVisibility: "ツールの表示範囲",
    settingsDetail:
      "ワークスペースのツールをどこまで表示するかを選びます。プロジェクトではなく、あなた自身の設定で、いつでも変更できます。",
  },
  emptyState: {
    gettingWorkspaceReady: "ワークスペースを準備しています。",
    couldNotCreate: "結果を作成できませんでした。",
    stopped: "停止しました。",
    readyToReview: "確認する準備ができました。",
    describeAppToBuild: "作りたいアプリを説明してください。",
    describeAppToDraft: "下書きにするアプリを説明してください。",
    working: "作業中",
    draft: "下書き",
    heroLineOne: "一行から始めましょう。",
    heroLineTwo: "使える出発点になります。",
    description:
      "作りたいアプリを説明すると、Coogo が実際に改善できる出発点にします。",
    workingPlaceholder: "Coogo が作業しています...",
    inputPlaceholder: "成果、対象ユーザー、制約を説明してください。",
    preparing: "準備中...",
    send: "送信",
    keepTalking: "会話を続けましょう。",
    conversationCanChange: "会話しながらアプリを変更できます。",
    pageDidNotPassChecks: "ページが確認を通過しませんでした",
    selectExactPart: "変更する箇所を正確に選択してください",
  },
  composePreview: {
    composing: "取得したブロックを組み立てています…",
    summary: (ready, total, pages) =>
      `${ready}/${total}件の候補を準備済み・${pages}ページを計画済み`,
  },
  backToProjects: "プロジェクトに戻る",
  chat: "チャット",
  close: "閉じる",
  context: "コンテキスト",
  closeContext: "コンテキストを閉じる",
  selectInApp: "アプリ上で選択する",
  pipelineStepper: {
    describe: "説明",
    scenarios: "シナリオ",
    pages: "画面",
    generate: "生成",
    complete: "完了",
  },
  releaseStepper: {
    journey: "リリースの流れ",
    steps: {
      review: {
        label: "確認",
        description: "準備できたものと、まだ対応が必要なものを確認します。",
      },
      prepare: {
        label: "準備",
        description: "承認できる候補をそのまま作成します。",
      },
      launch: {
        label: "公開",
        description: "承認して配信し、結果を確認します。",
      },
    },
    unavailable: (label) => `${label}（確認を通過するまで使えません）`,
  },
  cockpitTabSignal: {
    states: JA_COCKPIT_STATES,
    status: (label, kind) => `${label}：${JA_COCKPIT_STATES[kind]}`,
    unreviewed: (count) => `未確認の変更${count}件`,
  },
  worklog: {
    titles: {
      "webmcp-capabilities": "画面の操作",
      "production-evidence": "公開前チェック",
      "generation-provenance": "作られ方",
      "agent-action": "エージェント操作が完了しました",
    },
    entries: {
      "tools-ready": ({ count }) => ({
        label: "操作",
        value: `${count}件が利用できます`,
      }),
      "navigation-routes": ({ count }) => ({
        label: "画面遷移",
        value: `経路${count}件が使えます`,
      }),
      "actionflow-flows": ({ count }) => ({
        label: "動作",
        value: `エージェントが実行できる動作${count}件`,
      }),
      "form-tools": ({ count }) => ({
        label: "フォーム",
        value: `${count}件を送信できます`,
      }),
      "commerce-tools": ({ count }) => ({
        label: "カート",
        value: `カート操作${count}件が使えます`,
      }),
      "evidence-backed-blocks": ({ count, total }) => ({
        label: "確認済み",
        value: `${total ?? count}件中${count}件に確認された根拠があります`,
      }),
      "verified-references": ({ count }) => ({
        label: "参照",
        value: `検証済みの参照を${count}件使いました`,
      }),
      "review-transparent": () => ({
        label: "レビュー",
        value: "どう作られたかを確認できます",
      }),
      "review-needs-review": () => ({
        label: "レビュー",
        value: "公開する前にこのアプリを確認してください",
      }),
      "review-fallback-hidden": () => ({
        label: "レビュー",
        value: "作られ方の一部はまだ確認できません",
      }),
      "provenance-reused": ({ count }) => ({
        label: "再利用",
        value: `${count}件は検証済みデザインを再利用しています`,
      }),
      "provenance-fresh": ({ count }) => ({
        label: "新規",
        value: `${count}件は新しく作ったデザインです`,
      }),
    },
  },
  panels: {
    chat: {
      title: "チャット",
      description: "このワークスペースについて Coogo と話します",
      resize: "チャットパネルのサイズを変更",
    },
    inspector: {
      title: "詳細",
      description: "選択中の対象の詳細",
      resize: "詳細パネルのサイズを変更",
    },
    devtools: {
      title: "開発者ツール",
      description: "このプロジェクトの開発者ツール",
      resize: "開発者ツールパネルのサイズを変更",
    },
    pixels: (size) => `${size}ピクセル`,
  },
  failure: {
    selectExactPartDetail:
      "アプリを開いて変更したい文言かコントロールを選んでから、もう一度お試しください。",
  },
  details: "詳細",
  content: "コンテンツ",
  authoring: "テンプレート作成",
  showChat: "チャットを表示",
  conversation: (surface) => `${surface} の会話`,
  generating: (name) => `生成中: ${name}`,
  updating: (name, mode, status) => `${name} を更新中・${mode}・${status}`,
  real: "実データ",
  mock: "モック",
  cancel: "キャンセル",
  cancelGeneration: "生成をキャンセル",
  draftingStartingPoint: "開始案を準備しています。",
  applyingRequest: "リクエストを反映しています。",
  navigate: "移動",
  workspace: "ワークスペース",
  selectedTarget: '選択中の対象',
  changeTarget: '変更',
  currentWorkspace: '現在のワークスペース',
  currentPage: '現在のページ',
  currentItem: '現在の項目',
  workspacePane: "ワークスペースペイン",
  workspaceNavigation: "ワークスペースナビゲーション",
  workspaceDetails: "ワークスペース詳細",
  couldNotLoadProject: "プロジェクトを読み込めませんでした",
  projectNotOpenNothingToChange:
    "このプロジェクトは開けなかったため、ここには変更も公開もできる対象がありません。プロジェクト一覧に戻って、そこから開いてください。",
  publishNeedsLoadedProject:
    "このプロジェクトを開けなかったため、公開する対象がありません",
  publishBlockedGuidance: "公開画面で解決方法を確認してください。",
  readinessGateFailed: "準備状況の確認に失敗しました",
  connected: "接続済み",
  ready: "準備完了",
  saving: "保存中",
  unsaved: "未保存",
  saveFailed: "保存失敗",
  conflict: "競合",
  statusReady: "状態: 準備完了",
  statusSaving: "状態: 保存中",
  statusUnsaved: "状態: 未保存の変更",
  statusSaveFailed: "状態: 保存失敗",
  statusConflict: "状態: 保存競合",
  toggleStatusDetails: "状態の詳細を切り替え",
  currentWorkspaceMode: "現在のワークスペースモード",
  messageCoogo: "Coogo にメッセージ",
  askCoogo: "Coogo に質問",
  sourceReviewMenu: "レビュー",
  selectSomething: "アプリ内の項目を選択してください。",
  pickTargetHint:
    "作業する対象を選んでください。開くと画面・動作・詳細を扱えます。",
  openThisWork: "この作業を開く",
  noWorkToPick:
    "保存された作業がありません。ホームで始めるとここに表示されます。",
  askAboutThis: "この項目について質問",
  sendCurrentItem: "現在の項目をチャットに送ります。",
  viewDetails: "詳細を見る",
  openDeeperModel: "詳しいプロジェクトモデルを開きます。",
  contextHint:
    "チャットで質問すると会話を維持します。他の操作は必要な場合だけ専用パネルへ移動します。",
  screen: "画面",
  screenCount: (current, total) => `${total}件中${current}件`,
  editScreen: "画面を編集",
  searchScreens: "画面を検索",
  noScreensFound: "画面が見つかりません",
  generatedApp: "生成されたアプリ",
  generatingApp: "アプリを生成中",
  previewNotReadyTitle: "プレビューはまだ準備できていません",
  previewNotReadyDescription:
    "このプロジェクトにはページとブロックの組み合わせがありません。Design でページを接続するか、Coogo に不足しているアプリ画面の作成を依頼してください。",
  previewPreparingTitle: "プレビューを準備しています",
  previewPreparingDescription:
    "Coogo がこのアプリを分離されたプレビュー画面に接続しています。レンダリングの準備ができるまで、この状態を表示します。",
  previewUnavailableTitle: "プレビューレンダラーを利用できません",
  previewUnavailableDescription:
    "Coogo はプレビューレンダラーを開始できませんでした。Design でページとブロックの接続を確認してから、Coogo にプレビューの修復を依頼してください。",
  selectedForChat: "チャット対象に選択済み",
  noPartialResult: "部分結果はありません",
  checksFailed: (pageId) =>
    pageId ? `「${pageId}」ページの確認に失敗` : "確認に失敗",
  tryAgain: "再試行",
  startOver: "最初からやり直す",
  missReason: {
    "no-candidates": "一致するパターンが見つかりません",
    "no-template": "この種類はまだ準備できていません",
    "no-binding-schema": "データ構造を認識できません",
    "kg-init-failed": "少し時間をおいてもう一度お試しください",
    "bind-throw": "処理中に問題が発生しました",
    "wasm-unavailable": "このブラウザでは対応していません",
    aborted: "キャンセル済み",
    "unsupported-mode": "未対応のモードです",
    "backend-failed": "サーバー接続に失敗しました",
  },
  composer: {
    preview: "次のアプリ変更を説明",
    source: "ソースまたはフレームを確認",
    editor: "選択したページまたは要素を変更",
    behavior: "イベントまたはフローを追加・変更",
    data: "データソースまたはバインディングを変更",
    evidence: "検証する主張を入力",
    report: "状況レポートまたはエクスポートを変更",
    document: "選択した文書ページを変更",
    publish: "リリース準備を確認",
    deploy: "デプロイ先を確認",
    library: "再利用項目を確認",
    map: "ページ関係を変更",
    code: "Coogo JSON を確認・変更",
    settings: "プロジェクト設定を変更",
    documentPreview: "次の文書変更を説明",
    gamePreview: "次のゲーム変更を説明",
    askAbout: (task) => `${task} で必要な内容を入力`,
    askToReview: (task) => `${task} で確認したい内容を入力`,
  },
};

const zh: CoogoIdeChromeCopy = {
  ...en,
  surfaceLabels: {
    preview: "应用",
    source: "设计",
    editor: "页面",
    behavior: "动作",
    data: "数据",
    evidence: "检查",
    report: "现状",
    document: "指南",
    publish: "发布",
    deploy: "上线",
    library: "详细信息",
    map: "地图",
    code: "代码",
    settings: "设置",
  },
  settingsSurface: {
    eyebrow: '设置 · 项目',
    title: '项目设置',
    reviewSettings: '查看设置',
    summary: '发布前编辑项目的规范名称和说明。',
    general: '常规',
    projectName: '项目名称',
    untitledProject: '未命名项目',
    description: '说明',
    descriptionPlaceholder: '输入协作者需要了解的应用信息。',
    saved: '已保存',
    saveFailed: '无法保存项目设置',
    openLiveProject: '请打开实际项目后再编辑设置',
    metadataLoading: '正在加载项目信息',
    projectNameRequired: '必须填写项目名称',
    saving: '正在保存',
    saveChanges: '保存更改',
    slug: 'URL 标识',
    projectId: '项目 ID',
    buildTarget: '构建目标',
    platform: '平台',
    designSystemPreset: '设计系统预设',
    noneBound: '(未绑定)',
    dangerZone: '危险区域',
    dangerLead: '此工作区不提供项目删除和归档操作。请使用',
    dangerTail: '来归档或删除项目。',
  },
  generationProgress: {
    defaultTitle: '正在生成...',
    failedTitle: '应用生成失败',
    ariaLabel: '生成进度阶段',
    pageUnit: () => '页面',
    countAnnouncement: (status, count) => `${status}。${count} 个页面`,
    request: '请求',
    source: '来源',
    build: '构建',
    logic: '逻辑',
    app: '应用',
    launch: '上线',
    toneLabels: { done: '完成', active: '进行中', review: '待检查', pending: '等待中' },
    backgroundJob: {
      queued: '排队中',
      generating: '生成中',
      reconnecting: '正在重新连接',
      paused: '可以重新连接',
      continues: '离开 Studio 后，生成仍会继续。',
      resume: '恢复进度更新',
      resuming: '正在恢复...',
      resumeFailed: '无法恢复生成进度更新。',
      finalizationPending: '应用已保存 · 等待验证',
      finalizationDetail: '仅完成持久证据转换，不会重新生成应用。',
      finalize: '完成验证',
      finalizeFailed: '无法完成验证。已保存的应用未被重新生成。',
    },
    evidence: {
      source: '来源',
      logic: '逻辑',
      speed: '速度',
      launch: '上线',
      primitiveCount: (count) => `${count} 个基础元素`,
      conformance: '一致性',
      visible: '可见',
      design: '设计',
      compilerValid: '编译器有效',
      runtimeValid: '运行时有效',
      target: '目标',
    },
    statusLabels: {
      applied: '已应用',
      candidate: '候选',
      missing: '缺失',
      pass: '通过',
      blocked: '已阻止',
      warn: '警告',
      fail: '失败',
      'metric-only': '仅指标',
      ready: '就绪',
      'not-required': '无需',
      realtime: '实时',
      interactive: '交互',
      slow: '较慢',
      unknown: '未知',
    },
  },
  persona: {
    essentials: "基本工具",
    allTools: "所有工具",
    toolVisibility: "工具显示范围",
    settingsDetail:
      "选择工作区显示多少工具。这是你个人的偏好设置，而非某个项目的设置，随时可以更改。",
  },
  emptyState: {
    gettingWorkspaceReady: "正在准备工作区。",
    couldNotCreate: "无法创建结果。",
    stopped: "已停止。",
    readyToReview: "可以检查了。",
    describeAppToBuild: "请描述你想创建的应用。",
    describeAppToDraft: "请描述你想起草的应用。",
    working: "工作中",
    draft: "草稿",
    heroLineOne: "从一句话开始。",
    heroLineTwo: "得到一个可用的起点。",
    description: "描述你想要的应用，Coogo 会把它变成可以继续完善的起点。",
    workingPlaceholder: "Coogo 正在处理...",
    inputPlaceholder: "描述结果、目标用户和限制条件。",
    preparing: "准备中...",
    send: "发送",
    keepTalking: "继续对话。",
    conversationCanChange: "你可以通过对话继续更改应用。",
    pageDidNotPassChecks: "页面未通过检查",
    selectExactPart: "请选择要更改的确切部分",
  },
  composePreview: {
    composing: "正在组合检索到的区块…",
    summary: (ready, total, pages) =>
      `${ready}/${total} 个候选项已就绪 · 已规划 ${pages} 个页面`,
  },
  backToProjects: "返回项目",
  chat: "聊天",
  close: "关闭",
  context: "上下文",
  closeContext: "关闭上下文",
  selectInApp: "在应用中选择",
  pipelineStepper: {
    describe: "描述",
    scenarios: "场景",
    pages: "页面",
    generate: "生成",
    complete: "完成",
  },
  releaseStepper: {
    journey: "发布流程",
    steps: {
      review: {
        label: "检查",
        description: "查看已就绪的内容和仍需处理的内容。",
      },
      prepare: {
        label: "准备",
        description: "生成可供审批的确切候选版本。",
      },
      launch: {
        label: "发布",
        description: "审批、部署并验证发布结果。",
      },
    },
    unavailable: (label) => `${label}（通过检查后才能使用）`,
  },
  cockpitTabSignal: {
    states: ZH_COCKPIT_STATES,
    status: (label, kind) => `${label}：${ZH_COCKPIT_STATES[kind]}`,
    unreviewed: (count) => `${count} 项未确认的变更`,
  },
  worklog: {
    titles: {
      "webmcp-capabilities": "界面控制",
      "production-evidence": "上线检查",
      "generation-provenance": "生成方式",
      "agent-action": "代理操作已完成",
    },
    entries: {
      "tools-ready": ({ count }) => ({
        label: "控制",
        value: `${count} 项已就绪`,
      }),
      "navigation-routes": ({ count }) => ({
        label: "页面跳转",
        value: `${count} 条路径可用`,
      }),
      "actionflow-flows": ({ count }) => ({
        label: "行为",
        value: `代理可执行的行为 ${count} 项`,
      }),
      "form-tools": ({ count }) => ({
        label: "表单",
        value: `${count} 个可提交`,
      }),
      "commerce-tools": ({ count }) => ({
        label: "购物车",
        value: `${count} 项购物车操作可用`,
      }),
      "evidence-backed-blocks": ({ count, total }) => ({
        label: "已核对",
        value: `${total ?? count} 个部分中有 ${count} 个具备核对过的依据`,
      }),
      "verified-references": ({ count }) => ({
        label: "来源",
        value: `使用了 ${count} 项已验证的参考`,
      }),
      "review-transparent": () => ({
        label: "复查",
        value: "生成过程可以查看",
      }),
      "review-needs-review": () => ({
        label: "复查",
        value: "发布前请先复查这个应用",
      }),
      "review-fallback-hidden": () => ({
        label: "复查",
        value: "部分生成过程尚无法查看",
      }),
      "provenance-reused": ({ count }) => ({
        label: "复用",
        value: `${count} 个部分复用了已验证的设计`,
      }),
      "provenance-fresh": ({ count }) => ({
        label: "新建",
        value: `${count} 个部分是新建的设计`,
      }),
    },
  },
  panels: {
    chat: {
      title: "聊天",
      description: "就这个工作区与 Coogo 对话",
      resize: "调整聊天面板大小",
    },
    inspector: {
      title: "详情",
      description: "当前所选对象的详情",
      resize: "调整详情面板大小",
    },
    devtools: {
      title: "开发者工具",
      description: "本项目的开发者工具",
      resize: "调整开发者工具面板大小",
    },
    pixels: (size) => `${size} 像素`,
  },
  failure: {
    selectExactPartDetail: "打开应用，选中要改的文字或控件，然后再试一次。",
  },
  details: "详细信息",
  content: "内容",
  authoring: "模板创作",
  showChat: "显示聊天",
  conversation: (surface) => `${surface} 对话`,
  generating: (name) => `正在生成：${name}`,
  updating: (name, mode, status) => `正在更新 ${name} · ${mode} · ${status}`,
  real: "真实",
  mock: "模拟",
  cancel: "取消",
  cancelGeneration: "取消生成",
  draftingStartingPoint: "正在准备起点。",
  applyingRequest: "正在应用你的请求。",
  navigate: "导航",
  workspace: "工作区",
  selectedTarget: '已选目标',
  changeTarget: '更改',
  currentWorkspace: '当前工作区',
  currentPage: '当前页面',
  currentItem: '当前项目',
  workspacePane: "工作区面板",
  workspaceNavigation: "工作区导航",
  workspaceDetails: "工作区详细信息",
  couldNotLoadProject: "无法加载项目",
  projectNotOpenNothingToChange:
    "该项目没有打开，因此这里没有可修改或发布的对象。请返回项目列表，从那里打开。",
  publishNeedsLoadedProject: "该项目未能打开，因此没有可发布的对象",
  publishBlockedGuidance: "请在发布界面查看解决方法。",
  readinessGateFailed: "未通过就绪检查",
  connected: "已连接",
  ready: "就绪",
  saving: "正在保存",
  unsaved: "未保存",
  saveFailed: "保存失败",
  conflict: "冲突",
  statusReady: "状态：就绪",
  statusSaving: "状态：正在保存",
  statusUnsaved: "状态：有未保存的更改",
  statusSaveFailed: "状态：保存失败",
  statusConflict: "状态：保存冲突",
  toggleStatusDetails: "切换状态详细信息",
  currentWorkspaceMode: "当前工作区模式",
  messageCoogo: "给 Coogo 发消息",
  askCoogo: "询问 Coogo",
  sourceReviewMenu: "审阅",
  selectSomething: "请在应用中选择一个项目。",
  pickTargetHint:
    "请选择要处理的工作。打开后即可处理其页面、行为和详情。",
  openThisWork: "打开此工作",
  noWorkToPick:
    "尚无已保存的工作。从主页开始后会显示在这里。",
  askAboutThis: "询问此项目",
  sendCurrentItem: "将当前项目发送到聊天。",
  viewDetails: "查看详细信息",
  openDeeperModel: "打开更深入的项目模型。",
  contextHint: "在聊天中询问会保持对话。其他操作仅在需要时移到专用工作面板。",
  screen: "页面",
  screenCount: (current, total) => `${current} / ${total}`,
  editScreen: "编辑页面",
  searchScreens: "搜索页面",
  noScreensFound: "未找到页面",
  generatedApp: "生成的应用",
  generatingApp: "正在生成应用",
  previewNotReadyTitle: "预览尚未就绪",
  previewNotReadyDescription:
    "此项目没有页面和区块组合。请在 Design 中关联页面，或请 Coogo 创建缺失的应用界面。",
  previewPreparingTitle: "正在准备预览",
  previewPreparingDescription:
    "Coogo 正在将此应用连接到隔离的预览界面。在可渲染之前，此处会显示准备状态。",
  previewUnavailableTitle: "预览渲染器不可用",
  previewUnavailableDescription:
    "Coogo 无法启动预览渲染器。请在 Design 中检查页面和区块连接，然后请 Coogo 修复预览。",
  selectedForChat: "已选为聊天目标",
  noPartialResult: "没有部分结果",
  checksFailed: (pageId) => (pageId ? `页面“${pageId}”检查失败` : "检查失败"),
  tryAgain: "重试",
  startOver: "重新开始",
  missReason: {
    "no-candidates": "未找到匹配模式",
    "no-template": "此类型尚未准备好",
    "no-binding-schema": "无法识别数据结构",
    "kg-init-failed": "请稍后再试",
    "bind-throw": "处理时出现问题",
    "wasm-unavailable": "此浏览器不支持该功能",
    aborted: "已取消",
    "unsupported-mode": "不支持的模式",
    "backend-failed": "服务器连接失败",
  },
  composer: {
    preview: "描述下一项应用更改",
    source: "检查来源或画框",
    editor: "更改所选页面或元素",
    behavior: "添加或更改事件或流程",
    data: "更改数据源或绑定",
    evidence: "输入要验证的主张",
    report: "更改现状报告或导出",
    document: "更改所选文档页面",
    publish: "检查发布准备状态",
    deploy: "检查部署目标",
    library: "检查可复用项目",
    map: "更改页面关系",
    code: "检查或更改 Coogo JSON",
    settings: "更改项目设置",
    documentPreview: "描述下一项文档更改",
    gamePreview: "描述下一项游戏更改",
    askAbout: (task) => `描述你在${task}中需要的内容`,
    askToReview: (task) => `描述你想在${task}中检查的内容`,
  },
};

const zhTW: CoogoIdeChromeCopy = {
  ...zh,
  surfaceLabels: {
    preview: "應用",
    source: "設計",
    editor: "頁面",
    behavior: "動作",
    data: "資料",
    evidence: "檢查",
    report: "現況",
    document: "指南",
    publish: "發佈",
    deploy: "上線",
    library: "詳細資訊",
    map: "地圖",
    code: "程式碼",
    settings: "設定",
  },
  settingsSurface: {
    eyebrow: '設定 · 專案',
    title: '專案設定',
    reviewSettings: '檢視設定',
    summary: '發佈前編輯專案的規範名稱與說明。',
    general: '一般',
    projectName: '專案名稱',
    untitledProject: '未命名專案',
    description: '說明',
    descriptionPlaceholder: '輸入協作者需要了解的應用資訊。',
    saved: '已儲存',
    saveFailed: '無法儲存專案設定',
    openLiveProject: '請開啟實際專案後再編輯設定',
    metadataLoading: '正在載入專案資訊',
    projectNameRequired: '必須填寫專案名稱',
    saving: '正在儲存',
    saveChanges: '儲存變更',
    slug: 'URL 識別碼',
    projectId: '專案 ID',
    buildTarget: '建置目標',
    platform: '平台',
    designSystemPreset: '設計系統預設',
    noneBound: '(未綁定)',
    dangerZone: '危險區域',
    dangerLead: '此工作區不提供專案刪除與封存操作。請使用',
    dangerTail: '來封存或刪除專案。',
  },
  generationProgress: {
    defaultTitle: '正在產生...',
    failedTitle: '應用程式產生失敗',
    ariaLabel: '產生進度階段',
    pageUnit: () => '頁面',
    countAnnouncement: (status, count) => `${status}。${count} 個頁面`,
    request: '要求',
    source: '來源',
    build: '建置',
    logic: '邏輯',
    app: '應用',
    launch: '上線',
    toneLabels: { done: '完成', active: '進行中', review: '待檢查', pending: '等待中' },
    backgroundJob: {
      queued: '排隊中',
      generating: '產生中',
      reconnecting: '正在重新連線',
      paused: '可以重新連線',
      continues: '離開 Studio 後，產生仍會繼續。',
      resume: '恢復進度更新',
      resuming: '正在恢復...',
      resumeFailed: '無法恢復產生進度更新。',
      finalizationPending: '應用已儲存 · 等待驗證',
      finalizationDetail: '只完成持久證據轉換，不會重新產生應用。',
      finalize: '完成驗證',
      finalizeFailed: '無法完成驗證。已儲存的應用未被重新產生。',
    },
    evidence: {
      source: '來源',
      logic: '邏輯',
      speed: '速度',
      launch: '上線',
      primitiveCount: (count) => `${count} 個基礎元素`,
      conformance: '一致性',
      visible: '可見',
      design: '設計',
      compilerValid: '編譯器有效',
      runtimeValid: '執行階段有效',
      target: '目標',
    },
    statusLabels: {
      applied: '已套用',
      candidate: '候選',
      missing: '缺少',
      pass: '通過',
      blocked: '已封鎖',
      warn: '警告',
      fail: '失敗',
      'metric-only': '僅指標',
      ready: '就緒',
      'not-required': '不需要',
      realtime: '即時',
      interactive: '互動',
      slow: '較慢',
      unknown: '未知',
    },
  },
  persona: {
    essentials: "基本工具",
    allTools: "所有工具",
    toolVisibility: "工具顯示範圍",
    settingsDetail:
      "選擇工作區顯示多少工具。這是你個人的偏好設定，而非某個專案的設定，隨時可以更改。",
  },
  emptyState: {
    gettingWorkspaceReady: "正在準備工作區。",
    couldNotCreate: "無法建立結果。",
    stopped: "已停止。",
    readyToReview: "可以檢查了。",
    describeAppToBuild: "請描述你想建立的應用。",
    describeAppToDraft: "請描述你想起草的應用。",
    working: "工作中",
    draft: "草稿",
    heroLineOne: "從一句話開始。",
    heroLineTwo: "得到一個可用的起點。",
    description: "描述你想要的應用，Coogo 會把它變成可以繼續完善的起點。",
    workingPlaceholder: "Coogo 正在處理...",
    inputPlaceholder: "描述結果、目標使用者和限制條件。",
    preparing: "準備中...",
    send: "傳送",
    keepTalking: "繼續對話。",
    conversationCanChange: "你可以透過對話繼續變更應用。",
    pageDidNotPassChecks: "頁面未通過檢查",
    selectExactPart: "請選擇要變更的確切部分",
  },
  composePreview: {
    composing: "正在組合檢索到的區塊…",
    summary: (ready, total, pages) =>
      `${ready}/${total} 個候選項已就緒 · 已規劃 ${pages} 個頁面`,
  },
  backToProjects: "返回專案",
  chat: "聊天",
  close: "關閉",
  context: "上下文",
  closeContext: "關閉上下文",
  selectInApp: "在應用程式中選取",
  pipelineStepper: {
    describe: "描述",
    scenarios: "情境",
    pages: "頁面",
    generate: "產生",
    complete: "完成",
  },
  releaseStepper: {
    journey: "發佈流程",
    steps: {
      review: {
        label: "檢查",
        description: "查看已就緒的內容和仍需處理的內容。",
      },
      prepare: {
        label: "準備",
        description: "產生可供審核的確切候選版本。",
      },
      launch: {
        label: "發佈",
        description: "審核、部署並驗證發佈結果。",
      },
    },
    unavailable: (label) => `${label}（通過檢查後才能使用）`,
  },
  cockpitTabSignal: {
    states: ZH_TW_COCKPIT_STATES,
    status: (label, kind) => `${label}：${ZH_TW_COCKPIT_STATES[kind]}`,
    unreviewed: (count) => `${count} 項未確認的變更`,
  },
  worklog: {
    titles: {
      "webmcp-capabilities": "畫面控制",
      "production-evidence": "上線檢查",
      "generation-provenance": "產生方式",
      "agent-action": "代理操作已完成",
    },
    entries: {
      "tools-ready": ({ count }) => ({
        label: "控制",
        value: `${count} 項已就緒`,
      }),
      "navigation-routes": ({ count }) => ({
        label: "頁面切換",
        value: `${count} 條路徑可用`,
      }),
      "actionflow-flows": ({ count }) => ({
        label: "行為",
        value: `代理可執行的行為 ${count} 項`,
      }),
      "form-tools": ({ count }) => ({
        label: "表單",
        value: `${count} 個可送出`,
      }),
      "commerce-tools": ({ count }) => ({
        label: "購物車",
        value: `${count} 項購物車操作可用`,
      }),
      "evidence-backed-blocks": ({ count, total }) => ({
        label: "已核對",
        value: `${total ?? count} 個部分中有 ${count} 個具備核對過的依據`,
      }),
      "verified-references": ({ count }) => ({
        label: "來源",
        value: `使用了 ${count} 項已驗證的參考`,
      }),
      "review-transparent": () => ({
        label: "複查",
        value: "產生過程可以查看",
      }),
      "review-needs-review": () => ({
        label: "複查",
        value: "發佈前請先複查這個應用程式",
      }),
      "review-fallback-hidden": () => ({
        label: "複查",
        value: "部分產生過程尚無法查看",
      }),
      "provenance-reused": ({ count }) => ({
        label: "重複使用",
        value: `${count} 個部分重複使用了已驗證的設計`,
      }),
      "provenance-fresh": ({ count }) => ({
        label: "新建",
        value: `${count} 個部分是新建的設計`,
      }),
    },
  },
  panels: {
    chat: {
      title: "聊天",
      description: "就這個工作區與 Coogo 對話",
      resize: "調整聊天面板大小",
    },
    inspector: {
      title: "詳細資料",
      description: "目前所選對象的詳細資料",
      resize: "調整詳細資料面板大小",
    },
    devtools: {
      title: "開發者工具",
      description: "本專案的開發者工具",
      resize: "調整開發者工具面板大小",
    },
    pixels: (size) => `${size} 像素`,
  },
  failure: {
    selectExactPartDetail: "開啟應用程式，選取要變更的文字或控制項，然後再試一次。",
  },
  details: "詳細資訊",
  content: "內容",
  authoring: "範本創作",
  showChat: "顯示聊天",
  conversation: (surface) => `${surface} 對話`,
  generating: (name) => `正在產生：${name}`,
  updating: (name, mode, status) => `正在更新 ${name} · ${mode} · ${status}`,
  real: "真實",
  mock: "模擬",
  cancel: "取消",
  cancelGeneration: "取消產生",
  draftingStartingPoint: "正在準備起點。",
  applyingRequest: "正在套用你的要求。",
  navigate: "導覽",
  workspace: "工作區",
  selectedTarget: '已選目標',
  changeTarget: '變更',
  currentWorkspace: '目前工作區',
  currentPage: '目前頁面',
  currentItem: '目前項目',
  workspacePane: "工作區面板",
  workspaceNavigation: "工作區導覽",
  workspaceDetails: "工作區詳細資訊",
  couldNotLoadProject: "無法載入專案",
  projectNotOpenNothingToChange:
    "這個專案沒有開啟，因此這裡沒有可修改或發佈的對象。請回到專案清單，從那裡開啟。",
  publishNeedsLoadedProject: "這個專案未能開啟，因此沒有可發佈的對象",
  publishBlockedGuidance: "請在發佈畫面查看解決方法。",
  readinessGateFailed: "未通過就緒檢查",
  connected: "已連接",
  ready: "就緒",
  saving: "正在儲存",
  unsaved: "未儲存",
  saveFailed: "儲存失敗",
  conflict: "衝突",
  statusReady: "狀態：就緒",
  statusSaving: "狀態：正在儲存",
  statusUnsaved: "狀態：有未儲存的變更",
  statusSaveFailed: "狀態：儲存失敗",
  statusConflict: "狀態：儲存衝突",
  toggleStatusDetails: "切換狀態詳細資訊",
  currentWorkspaceMode: "目前工作區模式",
  messageCoogo: "傳訊息給 Coogo",
  askCoogo: "詢問 Coogo",
  sourceReviewMenu: "審閱",
  selectSomething: "請在應用中選擇一個項目。",
  pickTargetHint:
    "請選擇要處理的工作。開啟後即可處理其頁面、行為與詳細資訊。",
  openThisWork: "開啟此工作",
  noWorkToPick:
    "尚無已儲存的工作。從首頁開始後會顯示在這裡。",
  askAboutThis: "詢問此項目",
  sendCurrentItem: "將目前項目傳送到聊天。",
  viewDetails: "查看詳細資訊",
  openDeeperModel: "開啟更深入的專案模型。",
  contextHint: "在聊天中詢問會保留對話。其他操作只在需要時移到專用工作面板。",
  screen: "頁面",
  screenCount: (current, total) => `${current} / ${total}`,
  editScreen: "編輯頁面",
  searchScreens: "搜尋頁面",
  noScreensFound: "找不到頁面",
  generatedApp: "產生的應用程式",
  generatingApp: "正在產生應用程式",
  previewNotReadyTitle: "預覽尚未就緒",
  previewNotReadyDescription:
    "此專案沒有頁面與區塊組合。請在 Design 中連結頁面，或請 Coogo 建立缺少的應用畫面。",
  previewPreparingTitle: "正在準備預覽",
  previewPreparingDescription:
    "Coogo 正在將此應用連結到隔離的預覽介面。在可轉譯之前，這裡會顯示準備狀態。",
  previewUnavailableTitle: "預覽轉譯器無法使用",
  previewUnavailableDescription:
    "Coogo 無法啟動預覽轉譯器。請在 Design 中檢查頁面和區塊連結，然後請 Coogo 修復預覽。",
  selectedForChat: "已選為聊天目標",
  noPartialResult: "沒有部分結果",
  checksFailed: (pageId) => (pageId ? `頁面「${pageId}」檢查失敗` : "檢查失敗"),
  tryAgain: "重試",
  startOver: "重新開始",
  missReason: {
    "no-candidates": "找不到符合的模式",
    "no-template": "此類型尚未準備好",
    "no-binding-schema": "無法辨識資料結構",
    "kg-init-failed": "請稍後再試",
    "bind-throw": "處理時發生問題",
    "wasm-unavailable": "此瀏覽器不支援該功能",
    aborted: "已取消",
    "unsupported-mode": "不支援的模式",
    "backend-failed": "伺服器連線失敗",
  },
  composer: {
    ...zh.composer,
    preview: "描述下一項應用變更",
    source: "檢查來源或畫框",
    editor: "變更所選頁面或元素",
    behavior: "新增或變更事件或流程",
    data: "變更資料來源或綁定",
    evidence: "輸入要驗證的主張",
    report: "變更現況報告或匯出",
    document: "變更所選文件頁面",
    publish: "檢查發佈準備狀態",
    deploy: "檢查部署目標",
    library: "檢查可重用項目",
    map: "變更頁面關係",
    code: "檢查或變更 Coogo JSON",
    settings: "變更專案設定",
    documentPreview: "描述下一項文件變更",
    gamePreview: "描述下一項遊戲變更",
    askAbout: (task) => `描述你在${task}中需要的內容`,
    askToReview: (task) => `描述你想在${task}中檢查的內容`,
  },
};

export const COOGO_IDE_CHROME_COPY: Record<LocaleCode, CoogoIdeChromeCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};
